<script lang="ts" setup>
  import type { PlanPaymentMethod, SubscriberUsingAttemptRequest, SubscriptionAttempt } from '@/types'
  import { PAYMENT_METHODS } from '@/enum/payments.ts'
  import type { ValueOf } from 'type-fest'

  defineOptions({
    inheritAttrs: false,
  })

  const { planPaymentMethod } = defineProps<{
    planPaymentMethod: PlanPaymentMethod
  }>()

  const emit = defineEmits<{
    'payment-completed': [attempt: SubscriptionAttempt, paymentResponse: Record<string, unknown>]
    'payment-error': [error: unknown]
    'retry-attempt': [attempt: SubscriptionAttempt]
  }>()

  const forms: Record<ValueOf<typeof PAYMENT_METHODS>, ReturnType<typeof defineAsyncComponent>> = {
    [PAYMENT_METHODS.checkout]: defineAsyncComponent(() => import('@/components/payment/forms/checkout/Checkout.vue')),
    [PAYMENT_METHODS.idex]: defineAsyncComponent(() => import('@/components/payment/forms/idex/Idex.vue')),
    [PAYMENT_METHODS.tpay]: defineAsyncComponent(() => import('@/components/payment/forms/tpay/Tpay.vue')),
    [PAYMENT_METHODS.tamatem]: defineAsyncComponent(() => import('@/components/payment/forms/tamatem/Tamatem.vue')),
    [PAYMENT_METHODS.free]: defineAsyncComponent(() => import('@/components/payment/forms/free/Free.vue')),
    [PAYMENT_METHODS.paymob]: defineAsyncComponent(() => import('@/components/payment/forms/paymob/Paymob.vue')),
  } as const

  const paymentForm = forms[planPaymentMethod.payment_method_id]

  const trackStore = useTrackStore()

  const onError = (error: unknown) => {
    emit('payment-error', error)

    let message = 'Unknown error'

    if (error instanceof Error) {
      message = error.message
    } else if (typeof error === 'string') {
      message = error
    } else if (typeof error === 'object' && error !== null) {
      message = JSON.stringify(error)
    }

    trackStore.track('payment_error_presented', {
      payment_type: planPaymentMethod.sub_payment_method?.name,
      error: message,
      version: trackStore.subscriptionVersion,
    })
  }

  const handleRetryAttempt = (attempt: SubscriptionAttempt) => {
    emit('retry-attempt', attempt)
  }

  onMounted(() => {
    trackStore.track('payment_method_selected', {
      payment_type: planPaymentMethod.sub_payment_method?.name,
      version: trackStore.subscriptionVersion,
    })
  })
</script>

<template>
  <payment-form
    ref="paymentComp"
    :plan-payment-method="planPaymentMethod"
    @payment-completed="(attempt: SubscriptionAttempt, payload: SubscriberUsingAttemptRequest) => emit('payment-completed', attempt, payload)"
    @payment-error="onError"
    @retry-attempt="handleRetryAttempt"
  />
</template>
